import React, { useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { useHistory } from 'react-router-dom'
import { DSText, DSSpacer } from '@zoe/ds-web'
import { ScreenContainer } from '../core/layout/ScreenContainer'
import { onNextClickType } from '../types'
import { VISITED_SYMPTOM_RESULTS_KEY } from './SymptomScoreCalculatingScreen'

// Loading messages cycle once (no looping), one every `cycleMs`, then we advance.
const LOADING_MESSAGES = [
  'Analysing your symptom responses',
  'Calculating your inflammation levels',
  'Understanding your key symptoms',
  'Identifying how you can improve',
]

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const Card = styled.div`
  background: var(--colour-surface-cream-subtle);
  border-radius: 16px;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Ring = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  border: 6px solid #ede8de;
  border-top-color: #2d3246;
  animation: ${spin} 1.1s linear infinite;
`

const EmptyBar = styled.div`
  width: 100%;
  height: 12px;
  border-radius: 9999px;
  background: var(--colour-surface-cream-bold);
`

const BarLabelsRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const GhostCard = styled.div`
  border-radius: 12px;
  height: 64px;
  width: 100%;
  background: var(--colour-surface-cream-subtle);
`

export type SymptomScoreCalculatingScreenE7Props = {
  cycleMs?: number
}

type Props = SymptomScoreCalculatingScreenE7Props & {
  onNextClick: onNextClickType
}

export const SymptomScoreCalculatingScreenE7: React.FC<Props> = ({ cycleMs = 2000, onNextClick }) => {
  const history = useHistory()
  const [messageIndex, setMessageIndex] = useState(0)

  // Keep the latest onNextClick in a ref so the timer effect doesn't depend on
  // it — a re-created callback from the parent would otherwise tear down and
  // restart the advance timeout, which can stop the screen ever auto-advancing.
  const onNextClickRef = useRef(onNextClick)
  useEffect(() => {
    onNextClickRef.current = onNextClick
  }, [onNextClick])

  useEffect(() => {
    // Mirror the e5 calculating screen's back-navigation guard: if the user
    // pressed back from the results screen, step further back instead of
    // auto-advancing them straight forward again and trapping them in a loop.
    const cameBackFromResults =
      history.action === 'POP' && sessionStorage.getItem(VISITED_SYMPTOM_RESULTS_KEY) === 'true'
    if (cameBackFromResults) {
      sessionStorage.removeItem(VISITED_SYMPTOM_RESULTS_KEY)
      history.goBack()
      return
    }

    const interval = setInterval(() => {
      setMessageIndex((i) => (i < LOADING_MESSAGES.length - 1 ? i + 1 : i))
    }, cycleMs)
    // Advance once every message has been shown.
    const timeout = setTimeout(() => (onNextClickRef.current as () => void)(), cycleMs * LOADING_MESSAGES.length)
    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [cycleMs, history])

  return (
    <ScreenContainer>
      <Card>
        <DSText variant="fluid-paragraph-300" weight="regular" color="secondary" align="center">
          {LOADING_MESSAGES[messageIndex]}
        </DSText>
        <DSSpacer size={24} direction="vertical" />
        <Ring />
        <DSSpacer size={24} direction="vertical" />
        <EmptyBar />
        <DSSpacer size={8} direction="vertical" />
        <BarLabelsRow>
          <DSText variant="fluid-label-100" weight="regular">
            Moderate
          </DSText>
          <DSText variant="fluid-label-100" weight="regular">
            Severe
          </DSText>
        </BarLabelsRow>
      </Card>
      <DSSpacer size={32} direction="vertical" />
      <DSText variant="fluid-heading-500" weight="semiBold">
        Your key symptoms
      </DSText>
      <DSSpacer size={16} direction="vertical" />
      <GhostCard />
      <DSSpacer size={12} direction="vertical" />
      <GhostCard />
      <DSSpacer size={12} direction="vertical" />
      <GhostCard />
    </ScreenContainer>
  )
}
